import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import {
  getAssignment,
  createAssignment,
  updateAssignment,
} from "../../api/assignmentApi";
import { getGrades } from "../../api/gradeApi";
import { getSubjects } from "../../api/subjectApi";
import { getSessions } from "../../api/sessionApi";

const AssignmentFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    title: "",
    description: "",
    grade_id: null,
    subject_id: null,
    session_id: null,
    due_date: null,
    max_score: 100,
  });
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getGrades(), getSubjects(), getSessions()])
      .then(([g, s, ses]) => {
        setGrades(Array.isArray(g) ? g : g?.data || g?.items || []);
        setSubjects(Array.isArray(s) ? s : s?.data || s?.items || []);
        setSessions(Array.isArray(ses) ? ses : ses?.data || ses?.items || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    getAssignment(id).then((data) => {
      if (data?.success) {
        const a = data.assignment;
        setForm({
          title: a.title || "",
          description: a.description || "",
          grade_id: a.grade_id,
          subject_id: a.subject_id,
          session_id: a.session_id,
          due_date: a.due_date ? new Date(a.due_date) : null,
          max_score: a.max_score || 100,
        });
      }
    });
  }, [id, isEdit]);

  const handleSave = async () => {
    if (!form.title || !form.grade_id || !form.subject_id) {
      toast.error("Title, grade, and subject are required");
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form };
      if (isEdit) {
        await updateAssignment(id, payload);
        toast.success("Assignment updated");
      } else {
        await createAssignment(payload);
        toast.success("Assignment created");
      }
      navigate("/index/assignments");
    } catch (e) {
      toast.error("Failed to save assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2>{isEdit ? "Edit" : "New"} Assignment</h2>
      <div className="formgrid grid">
        <div className="field col-12">
          <label>Title *</label>
          <InputText
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full"
          />
        </div>
        <div className="field col-12">
          <label>Description</label>
          <InputTextarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4}
            autoResize
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Grade *</label>
          <Dropdown
            value={form.grade_id}
            options={grades}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setForm({ ...form, grade_id: e.value })}
            placeholder="Select grade"
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Subject *</label>
          <Dropdown
            value={form.subject_id}
            options={subjects}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setForm({ ...form, subject_id: e.value })}
            placeholder="Select subject"
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-4">
          <label>Session</label>
          <Dropdown
            value={form.session_id}
            options={sessions}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setForm({ ...form, session_id: e.value })}
            placeholder="Select session"
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-6">
          <label>Due Date</label>
          <Calendar
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.value })}
            showIcon
            dateFormat="yy-mm-dd"
            className="w-full"
          />
        </div>
        <div className="field col-12 md:col-6">
          <label>Max Score</label>
          <InputNumber
            value={form.max_score}
            onValueChange={(e) => setForm({ ...form, max_score: e.value })}
            className="w-full"
          />
        </div>
      </div>
      <div className="flex justify-content-end gap-2 mt-3">
        <Button
          label="Cancel"
          className="p-button-text"
          onClick={() => navigate("/index/assignments")}
        />
        <Button label="Save" icon="pi pi-save" onClick={handleSave} loading={saving} />
      </div>
    </Card>
  );
};

export default AssignmentFormPage;
